import { useEffect, useState } from "react";
import { Check, XCircle, QrCode, LogOut, RefreshCw, CheckCircle } from "lucide-react";
import axiosInstance from "../../../axiosConfig";
import { handleError } from "../../../services/handleError";
import { showToast } from "../../Toast";

export function NotificacoesWhatsapp() {
    const [instanciaConectada, setInstanciaConectada] = useState(false);
    const [mostraQrCode, setMostraQrCode] = useState(false);
    const [qrCode, setQrCode] = useState(null);
    const [loading, setLoading] = useState(false);
    const statusPedidos = ["Recebido", "Em preparo", "Pronto", "Saiu para entrega", "Entregue"];
    const [mensagens, setMensagens] = useState({});

    /* 
        Mensagens para usar de padrão whatsapp

        "Pendente": "Olá {{nomeCliente}}! Recebemos seu pedido #{{numeroPedido}} com sucesso. Anotamos tudo certinho: {{listaProdutos}}. Agora é só aguardar, que a mágica na cozinha já vai começar! ✨👨‍🍳",
        "Em preparo": "Agora sim, {{nomeCliente}}! Seu pedido #{{numeroPedido}} já entrou na cozinha e está sendo preparado com carinho. Em breve estará a caminho do seu paladar! 😊🍝",
        "Saiu para entrega": "Alerta de sabor chegando, {{nomeCliente}}! Seu pedido #{{numeroPedido}} saiu para entrega, quentinho e cheio de gostosuras: {{listaProdutos}}. Prepare-se para uma experiência deliciosa! 🚀🍕",
        "Finalizado": "{{nomeCliente}}, seu pedido #{{numeroPedido}} foi entregue com sucesso! Esperamos que tenha curtido cada mordida. Obrigado por nos escolher — até logo! 🙌🍴",
    */

    useEffect(() => {
        buscarStatusConexao();
        buscarMensagens();
    }, []);

    const buscarStatusConexao = () => {
        setLoading(true);
        axiosInstance.get("whatsapp/status")
            .then((response) => {
                setInstanciaConectada(response.data.conectado);
            })
            .catch((error) => handleError(error, "Erro ao buscar status conexão."))
            .finally(() => setLoading(false));
    };

    const handleVerificarAposScan = () => {
        setLoading(true);
        axiosInstance.get("whatsapp/status")
            .then((response) => {
                if (response.data.conectado) {
                    setInstanciaConectada(true);
                    setMostraQrCode(false);
                    setQrCode(null);
                    showToast("Sucesso!", "success", 3000, "Configurações salvas com sucesso!");
                } else {
                    handleError("A conexão ainda não foi estabelecida. Verifique se o QR Code foi escaneado corretamente no seu WhatsApp e tente novamente.");
                }
            })
            .catch((error) => {
                handleError(error, "Erro ao verificar status da conexão.");
            })
            .finally(() => setLoading(false));
    };

    const buscarMensagens = () => {
        setLoading(true);

        axiosInstance.get("WhatsappMensagem")
            .then((response) => {
                const mensagensConvertidas = {};

                response.data.forEach(item => {
                    const status = statusPedidos[item.statusPedido];
                    if (status) {
                        mensagensConvertidas[status] = item.mensagem;
                    }
                });

                setMensagens(prev => ({
                    ...prev,
                    ...mensagensConvertidas
                }));
            })
            .catch((error) => handleError(error, "Erro ao buscar mensagens automáticas."))
            .finally(() => setLoading(false));
    };

    const salvarMensagens = () => {
        setLoading(true);

        const mensagensConvertidas = statusPedidos.map((status, index) => ({
            statusPedido: index,
            mensagem: mensagens[status] ?? "",
        }));

        axiosInstance.post("WhatsappMensagem", { mensagens: mensagensConvertidas })
            .then(() => showToast("Sucesso!", "success", 3000, "Mensagens salvas com sucesso!"))
            .catch((error) => handleError(error, "Erro ao salvar mensagens automáticas."))
            .finally(() => setLoading(false));
    };

    const handleChangeMensagem = (status, value) => {
        setMensagens((prev) => ({
            ...prev,
            [status]: value,
        }));
    };

    const conectarInstancia = () => {
        setLoading(true);
        axiosInstance.post("whatsapp/conectar")
            .then((response) => {
                setMostraQrCode(true);
                setQrCode(response.data.qrCodeBase64);
            })
            .catch((error) => handleError(error, "Erro ao buscar status conexão."))
            .finally(() => setLoading(false));
    };

    const desconectarInstancia = () => {
        setLoading(true);
        axiosInstance.post("whatsapp/desconectar")
            .then(() => {
                setMostraQrCode(false);
                setQrCode(null);
                setInstanciaConectada(false);
            })
            .catch((error) => handleError(error, "Erro ao buscar status conexão."))
            .finally(() => setLoading(false));
    };

    return (
        <div className="w-full p-4 md:p-6 space-y-6 overflow-y-auto h-full">
            <div>
                <h2 className="text-2xl font-bold text-gray-900">Notificações via WhatsApp</h2>
                <p className="text-gray-700 mt-1">
                    Configure mensagens automáticas para avisar seus clientes sobre o status dos pedidos.
                </p>
            </div>

            {loading ? (
                <p className="text-gray-500 flex-grow flex items-center justify-center">Carregando...</p>
            ) : (
                <>
                    <div className="p-2 md:p-4 bg-white border border-zinc-300 rounded-lg w-full flex items-center justify-between">
                        <div className="flex items-center gap-1 text-sm text-zinc-700">
                            <span className="font-medium">Instância:</span>
                            {instanciaConectada ? (
                                <>
                                    <Check className="w-4 h-4 text-green-600" />
                                    <span className="text-green-700">Conectada</span>
                                </>
                            ) : (
                                <>
                                    <XCircle className="w-4 h-4 text-red-600" />
                                    <span className="text-red-700">Desconectada</span>
                                </>
                            )}
                        </div>

                        <div>
                            {!instanciaConectada && !mostraQrCode && (
                                <button
                                    onClick={conectarInstancia}
                                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                                >
                                    <QrCode className="w-4 h-4" />
                                    Conectar instância
                                </button>
                            )}

                            {!instanciaConectada && mostraQrCode && (
                                <button
                                    onClick={desconectarInstancia}
                                    className="inline-flex items-center gap-1 text-sm text-red-600 hover:underline"
                                >
                                    <LogOut className="w-4 h-4" />
                                    Cancelar conexão
                                </button>
                            )}

                            {instanciaConectada && (
                                <button
                                    onClick={desconectarInstancia}
                                    className="inline-flex items-center gap-1 text-sm text-red-600 hover:underline"
                                >
                                    <LogOut className="w-4 h-4" />
                                    Desconectar instância
                                </button>
                            )}
                        </div>
                    </div>

                    {!instanciaConectada && mostraQrCode && (
                        <div className="flex items-center justify-center border border-dashed border-zinc-300 rounded-xl p-6 bg-white w-full">
                            <div className="flex flex-col items-center">
                                <p className="text-sm text-zinc-600 mb-4 text-center">
                                    Escaneie o QR Code com o WhatsApp para conectar a instância:
                                </p>
                                <div className="bg-white rounded-lg w-72 h-72 flex items-center justify-center border p-1">
                                    {qrCode ? (
                                        <img
                                            src={qrCode}
                                            alt="QR Code para conectar instância do WhatsApp"
                                            className="w-full h-full object-contain"
                                        />
                                    ) : (
                                        <span className="text-zinc-500 text-sm">[ QR Code indisponível ]</span>
                                    )}
                                </div>

                                <button
                                    onClick={handleVerificarAposScan}
                                    className="mt-4 flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-md shadow-md hover:from-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors duration-200 disabled:opacity-60"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                            <span>Verificando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="w-5 h-5" />
                                            <span>Já escaneei, verificar</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {instanciaConectada && (
                        <>
                            <div className="flex flex-col gap-2 w-full">
                                <div className="text-sm h-full text-zinc-700">
                                    Você pode usar variáveis nas mensagens:
                                    <ul className="list-disc ml-5 mt-2 space-y-1">
                                        <li>
                                            <code className="bg-zinc-100 px-1 rounded text-zinc-800">
                                                {'{{nomeCliente}}'}
                                            </code>{" "}
                                            – Nome do cliente
                                        </li>
                                        <li>
                                            <code className="bg-zinc-100 px-1 rounded text-zinc-800">
                                                {'{{numeroPedido}}'}
                                            </code>{" "}
                                            – Número do pedido
                                        </li>
                                        <li>
                                            <code className="bg-zinc-100 px-1 rounded text-zinc-800">
                                                {'{{listaProdutos}}'}
                                            </code>{" "}
                                            – Produtos do pedido
                                        </li>
                                        <li>
                                            <code className="bg-zinc-100 px-1 rounded text-zinc-800">
                                                {'{{tipoEntrega}}'}
                                            </code>{" "}
                                            – Tipo de entrega
                                        </li>
                                        <li>
                                            <code className="bg-zinc-100 px-1 rounded text-zinc-800">
                                                {'{{valorTotal}}'}
                                            </code>{" "}
                                            – Valor do pedido
                                        </li>
                                        <li>
                                            <code className="bg-zinc-100 px-1 rounded text-zinc-800">
                                                {'{{estimativa}}'}
                                            </code>{" "}
                                            – Estimativa total
                                        </li>
                                        <li>
                                            <code className="bg-zinc-100 px-1 rounded text-zinc-800">
                                                {'{{estimativaEntrega}}'}
                                            </code>{" "}
                                            – Estimativa de entrega
                                        </li>
                                        <li>
                                            <code className="bg-zinc-100 px-1 rounded text-zinc-800">
                                                {'{{formaPagamento}}'}
                                            </code>{" "}
                                            – Forma de Pagamento
                                        </li>
                                        <li>
                                            <code className="bg-zinc-100 px-1 rounded text-zinc-800">
                                                {'{{\\n}}'}
                                            </code>{" "}
                                            – Pular linha
                                        </li>
                                    </ul>
                                </div>

                                <div className="space-y-2 max-h-full overflow-y-auto">
                                    {statusPedidos.map((status) => (
                                        <div key={status} className="space-y-1">
                                            <label className="text-sm font-semibold text-zinc-800">
                                                Mensagem para <span className="text-blue-600">"{status}"</span>
                                            </label>
                                            <textarea
                                                rows={3}
                                                value={mensagens[status] ?? ""}
                                                onChange={(e) => handleChangeMensagem(status, e.target.value)}
                                                className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="bg-white flex justify-end">
                                    <button
                                        onClick={salvarMensagens}
                                        disabled={loading}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        Salvar
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
